// UPDATED FILE: frontend/src/components/WebcamCapture.jsx
import React, { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';

const WebcamCapture = ({ onCapture, isCapturing = false }) => {
  const webcamRef = useRef(null);
  const [facingMode, setFacingMode] = useState('user');
  const [cameraError, setCameraError] = useState(null);

  const videoConstraints = {
    facingMode: facingMode,
    width: { ideal: 640 },
    height: { ideal: 480 }
  };

  const capture = useCallback(() => {
    if (webcamRef.current) {
      try {
        const imageSrc = webcamRef.current.getScreenshot({
          width: 640,
          height: 480,
          screenshotQuality: 0.8
        });
        
        if (!imageSrc) {
          throw new Error('Failed to capture image');
        }
        
        onCapture(imageSrc);
      } catch (error) {
        console.error('Capture error:', error);
        alert('Failed to capture image. Please try again.');
      }
    }
  }, [webcamRef, onCapture]);

  const toggleCamera = () => {
    setFacingMode(prevMode => prevMode === 'user' ? 'environment' : 'user');
  };

  const handleUserMediaError = (error) => {
    console.error('Webcam error:', error);
    setCameraError('Camera access denied or not available.');
  };

  return (
    <div className="space-y-6">
      {/* Camera Preview */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-white/20 shadow-2xl">
        {cameraError ? (
          <div className="aspect-video flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 text-white p-8">
            <div className="text-5xl mb-4">📷</div>
            <div className="text-center">
              <p className="font-semibold text-lg mb-2">Camera Unavailable</p>
              <p className="text-gray-300 text-sm">{cameraError}</p>
            </div>
          </div>
        ) : (
          <>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              className="w-full h-auto"
              mirrored={facingMode === 'user'}
              onUserMediaError={handleUserMediaError}
            />
            
            {/* Face guide overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-56 h-56 border-3 border-white/40 border-dashed rounded-full animate-pulse"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="w-40 h-40 border border-white/30 rounded-full"></div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Camera Controls */}
      <div className="grid grid-cols-2 gap-3">
        <button 
          onClick={toggleCamera}
          className="flex items-center justify-center py-3 px-4 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl font-medium hover:from-gray-200 hover:to-gray-300 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300/50"
          disabled={isCapturing || cameraError}
        >
          <span className="mr-2 text-lg">🔄</span>
          Switch Camera
        </button>
        
        <button 
          onClick={capture}
          className="flex items-center justify-center py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all active:scale-[0.98] shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isCapturing || cameraError}
        >
          {isCapturing ? (
            <>
              <span className="mr-2 animate-spin">⏳</span>
              Processing...
            </>
          ) : (
            <>
              <span className="mr-2 text-lg">📸</span>
              Capture Selfie
            </>
          )}
        </button>
      </div>
      
      {/* Camera Error Message */}
      {cameraError && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50/50 rounded-xl p-4 border border-yellow-200/50">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center mr-3 mt-0.5">
              <span className="text-yellow-600 text-sm">!</span>
            </div>
            <div>
              <p className="font-medium text-yellow-800 mb-1">Camera Issue Detected</p>
              <p className="text-sm text-yellow-700">
                {cameraError} Please check camera permissions and try refreshing the page.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Instructions */}
      <div className="text-center">
        <p className="text-sm text-gray-600 font-medium">
          Position your face in the circle and ensure good lighting
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Look directly at the camera and keep a neutral expression
        </p>
      </div>
    </div>
  );
};

export default WebcamCapture;